import * as React from "react"
import { connect } from 'react-redux'
import FileInputComponent from 'react-file-input-previews-base64'
import trans from 'trans'
import { AxiosResponse } from 'axios';
import store from "store";
import { Link as RouterLink } from "react-router-dom"

import {
    css,
    Grid,
    GridCell,
    Icon,
    Theme,
    Message,
    Button,
} from "@bdfx/mergado-ui-kit";

import { createToast } from "actions";
import toastFactory from "app/helpers/toast_factory";

import {
    GET_GRADIENTS,
    setGradientsToStore,
} from "template-edit/reducers";

import {
    updateInputValue as change,
} from "app/reducers/form";

import { Gradient, Template } from "app/types/model-types";
import { eshopId, exportGradients, getGradients, importGradients, projectId } from "app/helpers/api";
import { GradientColorPicker } from "shape-edit/forms/gradient-color-picker";
import { GradientSvg } from "shape-edit/forms/fields";
import { ALLOWED_FILE_TYPES, handleExportGradients, MAX_FILE_SIZE, RemoveGradientConfirm } from "shape-edit/forms/select-gradient";
import { ID, ReduxList } from "app/types/app-types";
import { find } from "lodash";
import { URL } from "app/config/api-config";
import { checkGradientsUsage, GradientUsageType } from "shape-edit/forms/helpers";
import { FileInputType } from "template-list/containers/template-list";

export const initGradient: Gradient = {
    type: 'linear',
    x1: 0,
    y1: 0,
    x2: 100,
    y2: 0,
    stop_points: [
        { offset: 0, color: '#ffffff' },
        { offset: 100, color: '#0000ff' },
    ]
}

interface Props {
    createToast: Function
    change: typeof change
    setGradientsToStore: typeof setGradientsToStore
    gradients: Gradient[]
    templates: ReduxList<Template>
}

interface State {
    gradient: Gradient
    gradientUsage: GradientUsageType[]
    isLoading: boolean
}

class Gradients extends React.Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            gradient: initGradient,
            isLoading: false,
            gradientUsage: [],
        }
    }

    componentDidMount() {
        this.updateGradients()
    }

    updateGradients = () => {
        this.setState({ isLoading: true })

        getGradients()
            .then((response: AxiosResponse<Gradient[]>) => {
                this.props.setGradientsToStore(response.data)
            })
            .catch(error => {
                store.dispatch(createToast(toastFactory(GET_GRADIENTS.ERROR, error)))
            })
            .finally(() => {
                this.setState({ isLoading: false })
            })

        this.setState({gradientUsage: checkGradientsUsage(this.props.templates)})
    }

    handleDeleteGradient = (gradient: Gradient) => {
        RemoveGradientConfirm(gradient, this.updateGradients)
    }

    importGradients = (file: FileInputType) => {
        if (file.size <= MAX_FILE_SIZE && ALLOWED_FILE_TYPES.includes(file.type)) {
            importGradients(file.base64, file.name)
                .then((response: AxiosResponse<Gradient[]>) => {
                    this.props.setGradientsToStore(response.data)
                })
                .catch(error => {
                    console.error('cannot import gradient', error)
                })
        } else {
            console.error(`Unsupported file: ${file.type}, size: ${file.size}`)
        }
    }

    render() {
        const { gradient, isLoading, gradientUsage } = this.state
        const { gradients } = this.props

        const selectedGradientIsUsed = find(this.state.gradientUsage, (u: GradientUsageType) => {
            return `${u.gradientId}` === `${gradient.id}`
        })

        return <StyledContent>
            <GridCell className="gradient-picker">
                <GradientColorPicker
                    updateGradients={this.updateGradients}
                    gradient={gradient}
                    key={gradient.id}
                    gradients={gradients}
                />
            </GridCell>

            <GridCell className="all-gradients">
                {(gradients && Array.isArray(gradients) && gradients.length > 0) ?
                    <Grid cols={'repeat(auto-fit, 100px)'} className={isLoading ? 'muk-helper-loading' : ''}>
                        {gradients.map((g, key) => {
                            const isUsed = find(this.state.gradientUsage, (u: GradientUsageType) => {
                                return `${u.gradientId}` === `${g.id}`
                            })

                            return <GridCell key={`gradient-${key}`} className={'centered'}>
                                <div className={'clickable'} onClick={() => this.setState({ gradient: g })} style={{ position: 'relative' }}>
                                    <GradientSvg gradient={g} idx={key} width={100} height={100} isActive={gradient && gradient.id && gradient.id === g.id} />
                                    {g.id && !isUsed &&
                                        <span style={{ position: 'absolute', right: '16px', top: '4px', cursor: 'pointer' }} onClick={() => this.handleDeleteGradient(g)}>
                                            <Icon type={'trash'} color={Theme.red} size={18} />
                                        </span>
                                    }

                                    <span title={trans('export_gradients')} style={{ position: 'absolute', left: '6px', top: '4px', cursor: 'pointer' }} onClick={() => handleExportGradients([g.id])}>
                                        <Icon type={'download-radix'} color={Theme.steelblue} size={18} />
                                    </span>

                                </div>
                            </GridCell>
                        }
                        )}
                    </Grid> :
                    <Message type={"info"} closeable={true} icon={<Icon type={'info'} color={Theme.blue} size={28} />}>
                        {trans('no_gradients_yet')}
                    </Message>
                }

                {selectedGradientIsUsed &&
                    <Message type={'info'} closeable={true} icon={<Icon type={'info'} color={Theme.blue} size={28} />}>
                        {trans('gradient_is_used_by_template')}

                        <div style={{ textAlign: 'center' }}>
                            <RouterLink to={`/eshop/${eshopId}/project/${projectId}/templates/${selectedGradientIsUsed.templateId}/edit`}>
                                <img src={`${URL.laravelBase}images/thumbs/thumbnail${selectedGradientIsUsed.templateId}.png`} style={{ maxWidth: "400px", maxHeight: "400px" }} />
                            </RouterLink>
                        </div>
                    </Message>
                }

                <FileInputComponent
                    labelText={null}
                    imagePreview={false}
                    multiple={false}
                    buttonComponent={
                        <Button
                            type={'void'}
                            secondary={true}
                            icon={<Icon type={'upload-radix'} text={trans("import_gradients")} color={Theme.steelblue} />}
                            text={trans('import_gradients')}
                            style={{ marginBottom: '20px' }}
                        />
                    }
                    callbackFunction={this.importGradients}
                    accept="application/json"
                />
            </GridCell>
        </StyledContent>
    }
}

const StyledContent = css(Grid)({
    gridTemplateColumns: '600px auto',
    gridGap: '20px',
    margin: '20px',
    ' .muk-gridcell': {
        textAlign: 'center',
    },
    overflowX: 'auto',
    '@media screen and (max-width: 700px)': {
        gridTemplateColumns: 'auto',
    },
})

function mapStateToProps(state) {
    return {
        gradients: state.gradients,
        templates: state.templates,
    }
}

export default connect(mapStateToProps, {
    createToast,
    change,
    setGradientsToStore,
})(Gradients)
